/**
 * Command Pattern Types for UI-Model-1 (API-Direct Approach)
 *
 * Commands operate directly on the API format structure.
 * Undo/redo uses snapshot-based approach for simplicity.
 */

import {
  UIModelState,
  ContainerAPI,
  FieldAPI,
  WidgetAPI,
  GroupAPI,
  PageAPI,
  ContainerConfigurationAPI,
  ItemAPI,
  DirtySection,
  SubmodelAPI,
  SubmodelFullAPI,
  NavigationConfigAPI,
} from './state.types';
import { HeaderConfigurationAPI, FooterConfigurationAPI, GroupConfigurationAPI, PageConfigurationAPI, ContainerItemAPI } from './api.types';

// ============================================================================
// Base Command Interface
// ============================================================================

export interface Command {
  readonly type: string;
  readonly timestamp: number;
  readonly description: string;
}

export interface ExecutableCommand<T extends Command = Command> {
  command: T;
  execute(state: UIModelState): UIModelState;
  undo(state: UIModelState): UIModelState;
}

// ============================================================================
// Container Commands (operate on canvas.containers[])
// ============================================================================

export interface AddContainerCommand extends Command {
  type: 'ADD_CONTAINER';
  payload: {
    container: ContainerAPI;
    canvasId: string;
    insertIndex?: number;
    parentContainerId?: string;  // For nested containers
  };
}

export interface RemoveContainerCommand extends Command {
  type: 'REMOVE_CONTAINER';
  payload: {
    containerId: string;
    canvasId: string;
    // Stored for undo - the removed container and all its descendants
    removedContainer?: ContainerAPI;
    parentContainerId?: string;
    // Also stored for undo - items and child containers
    removedItems?: ItemAPI[];
    removedChildContainers?: ContainerAPI[];
  };
}

export interface UpdateContainerCommand extends Command {
  type: 'UPDATE_CONTAINER';
  payload: {
    containerId: string;
    canvasId: string;
    updates: Partial<{
      name: string;
      order: number;
      configuration: Partial<ContainerConfigurationAPI>;
      submodel: SubmodelAPI | null;
      /** Free style: full list of bound submodels (first mirrors `submodel`) */
      submodels: SubmodelFullAPI[] | null;
      items: ContainerItemAPI[];
      rowIndex: number;
      metadata: Partial<ContainerAPI['metadata']>;
    }>;
    // Stored for undo
    previousValues?: Partial<ContainerAPI>;
  };
}

export interface MoveContainerCommand extends Command {
  type: 'MOVE_CONTAINER';
  payload: {
    containerId: string;
    fromCanvasId: string;
    toCanvasId: string;
    fromIndex: number;
    toIndex: number;
    fromParentId: string | null;
    toParentId: string | null;
  };
}

export interface ReorderContainersCommand extends Command {
  type: 'REORDER_CONTAINERS';
  payload: {
    canvasId: string;
    parentId: string | null;  // null for top-level containers
    containerIds: string[];   // New order
    // Stored for undo
    previousOrder?: string[];
  };
}

/**
 * Placement mode for container duplication
 */
export type DuplicatePlacement = 'same-row' | 'new-row';

export interface DuplicateContainerCommand extends Command {
  type: 'DUPLICATE_CONTAINER';
  payload: {
    sourceContainerId: string;
    canvasId: string;
    parentContainerId?: string;
    duplicatedContainer: ContainerAPI;
    duplicatedContainerId: string;
    /** Placement mode: 'same-row' adds to source row, 'new-row' creates row below */
    placement?: DuplicatePlacement;
  };
}

// ============================================================================
// Item Commands (operate on container.content.items[])
// ============================================================================

export interface AddItemCommand extends Command {
  type: 'ADD_ITEM';
  payload: {
    item: ItemAPI;
    canvasId: string;
    containerId: string;
    insertIndex?: number;
  };
}

export interface RemoveItemCommand extends Command {
  type: 'REMOVE_ITEM';
  payload: {
    itemId: string;
    canvasId: string;
    containerId: string;
    // Stored for undo
    removedItem?: ItemAPI;
    removedIndex?: number;
    // Source container's binding before removal — restored on undo, since
    // clearEmptySubmodelRefs releases it when the last direct item goes.
    previousSubmodel?: SubmodelAPI;
    previousSubmodels?: SubmodelFullAPI[];
  };
}

export interface UpdateItemCommand extends Command {
  type: 'UPDATE_ITEM';
  payload: {
    itemId: string;
    canvasId: string;
    containerId: string;
    updates: Partial<ItemAPI>;
    // Stored for undo
    previousValues?: Partial<ItemAPI>;
  };
}

export interface MoveItemCommand extends Command {
  type: 'MOVE_ITEM';
  payload: {
    itemId: string;
    fromCanvasId: string;
    toCanvasId: string;
    fromContainerId: string;
    toContainerId: string;
    fromIndex: number;
    toIndex: number;
  };
}

export interface ReorderItemsCommand extends Command {
  type: 'REORDER_ITEMS';
  payload: {
    canvasId: string;
    containerId: string;
    itemIds: string[];  // New order
    // Stored for undo
    previousOrder?: string[];
  };
}

// ============================================================================
// Navigation Commands (Groups & Pages)
// ============================================================================

export interface AddGroupCommand extends Command {
  type: 'ADD_GROUP';
  payload: {
    group: GroupAPI;
    insertIndex?: number;
  };
}

export interface RemoveGroupCommand extends Command {
  type: 'REMOVE_GROUP';
  payload: {
    groupId: string;
    // Stored for undo - includes all pages and their canvases
    removedGroup?: GroupAPI;
    removedCanvases?: Record<string, ContainerAPI[]>;
  };
}

export interface UpdateGroupCommand extends Command {
  type: 'UPDATE_GROUP';
  payload: {
    groupId: string;
    updates: Partial<Pick<GroupAPI, 'name' | 'orientation' | 'configuration'>>;
    // Stored for undo
    previousValues?: Partial<GroupAPI>;
  };
}

export interface ReorderGroupsCommand extends Command {
  type: 'REORDER_GROUPS';
  payload: {
    groupIds: string[];  // New order
    // Stored for undo
    previousOrder?: string[];
  };
}

export interface AddPageCommand extends Command {
  type: 'ADD_PAGE';
  payload: {
    page: PageAPI;
    groupId: string;
    insertIndex?: number;
  };
}

export interface RemovePageCommand extends Command {
  type: 'REMOVE_PAGE';
  payload: {
    pageId: string;
    groupId: string;
    // Stored for undo
    removedPage?: PageAPI;
    removedCanvas?: ContainerAPI[];
  };
}

export interface UpdatePageCommand extends Command {
  type: 'UPDATE_PAGE';
  payload: {
    pageId: string;
    groupId: string;
    updates: Partial<Pick<PageAPI, 'name' | 'configuration'>>;
    // Stored for undo
    previousValues?: Partial<PageAPI>;
  };
}

export interface ReorderPagesCommand extends Command {
  type: 'REORDER_PAGES';
  payload: {
    groupId: string;
    pageIds: string[];  // New order
    // Stored for undo
    previousOrder?: string[];
  };
}

export interface MovePageBetweenGroupsCommand extends Command {
  type: 'MOVE_PAGE_BETWEEN_GROUPS';
  payload: {
    pageId: string;
    fromGroupId: string;
    toGroupId: string;
    toIndex: number;
    // Stored for undo
    fromIndex?: number;
  };
}

export interface DuplicatePageCommand extends Command {
  type: 'DUPLICATE_PAGE';
  payload: {
    pageId: string;
    groupId: string;
    newPageId: string;
    newCanvasId: string;
    // Stored for undo
    duplicatedPage?: PageAPI;
  };
}

// ============================================================================
// Navigation Config Command
// ============================================================================

export interface UpdateNavigationConfigCommand extends Command {
  type: 'UPDATE_NAVIGATION_CONFIG';
  payload: {
    updates: Partial<NavigationConfigAPI>;
    // Stored for undo
    previousValues?: Partial<NavigationConfigAPI>;
  };
}

// ============================================================================
// Header Commands
// ============================================================================

export interface UpdateHeaderCommand extends Command {
  type: 'UPDATE_HEADER';
  payload: {
    updates: Partial<HeaderConfigurationAPI>;
    // Stored for undo
    previousValues?: Partial<HeaderConfigurationAPI>;
  };
}

// ============================================================================
// Footer Commands
// ============================================================================

export interface UpdateFooterCommand extends Command {
  type: 'UPDATE_FOOTER';
  payload: {
    updates: Partial<FooterConfigurationAPI>;
    previousValues?: Partial<FooterConfigurationAPI>;
  };
}

// ============================================================================
// Batch Command (for grouping related changes)
// ============================================================================

export interface BatchCommand extends Command {
  type: 'BATCH';
  payload: {
    commands: UndoableCommand[];
    batchDescription: string;
  };
}

// ============================================================================
// Selection Commands (not undoable, but tracked)
// ============================================================================

export interface SelectContainerCommand extends Command {
  type: 'SELECT_CONTAINER';
  payload: {
    containerId: string | null;
    canvasId?: string;
    path?: string[];
  };
}

export interface SelectItemCommand extends Command {
  type: 'SELECT_ITEM';
  payload: {
    itemId: string | null;
    itemType: string | null;
    containerId?: string;
    canvasId?: string;
  };
}

export interface SelectSubmodelCommand extends Command {
  type: 'SELECT_SUBMODEL';
  payload: {
    submodelId: string | null;
    containerId?: string;
  };
}

export interface ClearSelectionCommand extends Command {
  type: 'CLEAR_SELECTION';
  payload: Record<string, never>;
}

export interface SetActivePageCommand extends Command {
  type: 'SET_ACTIVE_PAGE';
  payload: {
    groupId: string;
    pageId: string;
  };
}

// ============================================================================
// State Management Commands
// ============================================================================

export interface SetModelCommand extends Command {
  type: 'SET_MODEL';
  payload: {
    model: UIModelState['model'];
  };
}

export interface MarkDirtyCommand extends Command {
  type: 'MARK_DIRTY';
  payload: {
    section: DirtySection;
    canvasId?: string;
  };
}

export interface MarkCleanCommand extends Command {
  type: 'MARK_CLEAN';
  payload: {
    section: DirtySection;
    canvasId?: string;
  };
}

// ============================================================================
// Union Types
// ============================================================================

export type ContainerCommand =
  | AddContainerCommand
  | RemoveContainerCommand
  | UpdateContainerCommand
  | MoveContainerCommand
  | ReorderContainersCommand
  | DuplicateContainerCommand;

export type ItemCommand =
  | AddItemCommand
  | RemoveItemCommand
  | UpdateItemCommand
  | MoveItemCommand
  | ReorderItemsCommand;

export type NavigationCommand =
  | AddGroupCommand
  | RemoveGroupCommand
  | UpdateGroupCommand
  | ReorderGroupsCommand
  | AddPageCommand
  | RemovePageCommand
  | UpdatePageCommand
  | ReorderPagesCommand
  | MovePageBetweenGroupsCommand
  | DuplicatePageCommand
  | UpdateNavigationConfigCommand;

export type HeaderCommand = UpdateHeaderCommand;

export type FooterCommand = UpdateFooterCommand;

export type SelectionCommand =
  | SelectContainerCommand
  | SelectItemCommand
  | SelectSubmodelCommand
  | ClearSelectionCommand
  | SetActivePageCommand;

export type StateCommand =
  | SetModelCommand
  | MarkDirtyCommand
  | MarkCleanCommand;

export type UndoableCommand =
  | ContainerCommand
  | ItemCommand
  | NavigationCommand
  | HeaderCommand
  | FooterCommand
  | BatchCommand;

export type UIModelCommand =
  | UndoableCommand
  | SelectionCommand
  | StateCommand;

// ============================================================================
// Command Creators
// ============================================================================

export const createAddContainerCommand = (
  payload: AddContainerCommand['payload'],
  description: string
): AddContainerCommand => ({
  type: 'ADD_CONTAINER',
  timestamp: Date.now(),
  description,
  payload,
});

export const createRemoveContainerCommand = (
  payload: RemoveContainerCommand['payload'],
  description: string
): RemoveContainerCommand => ({
  type: 'REMOVE_CONTAINER',
  timestamp: Date.now(),
  description,
  payload,
});

export const createUpdateContainerCommand = (
  payload: UpdateContainerCommand['payload'],
  description: string
): UpdateContainerCommand => ({
  type: 'UPDATE_CONTAINER',
  timestamp: Date.now(),
  description,
  payload,
});

export const createMoveContainerCommand = (
  payload: MoveContainerCommand['payload'],
  description: string
): MoveContainerCommand => ({
  type: 'MOVE_CONTAINER',
  timestamp: Date.now(),
  description,
  payload,
});

export const createDuplicateContainerCommand = (
  payload: DuplicateContainerCommand['payload'],
  description: string
): DuplicateContainerCommand => ({
  type: 'DUPLICATE_CONTAINER',
  timestamp: Date.now(),
  description,
  payload,
});

export const createAddItemCommand = (
  payload: AddItemCommand['payload'],
  description: string
): AddItemCommand => ({
  type: 'ADD_ITEM',
  timestamp: Date.now(),
  description,
  payload,
});

export const createRemoveItemCommand = (
  payload: RemoveItemCommand['payload'],
  description: string
): RemoveItemCommand => ({
  type: 'REMOVE_ITEM',
  timestamp: Date.now(),
  description,
  payload,
});

export const createUpdateItemCommand = (
  payload: UpdateItemCommand['payload'],
  description: string
): UpdateItemCommand => ({
  type: 'UPDATE_ITEM',
  timestamp: Date.now(),
  description,
  payload,
});

export const createMoveItemCommand = (
  payload: MoveItemCommand['payload'],
  description: string
): MoveItemCommand => ({
  type: 'MOVE_ITEM',
  timestamp: Date.now(),
  description,
  payload,
});

export const createReorderItemsCommand = (
  payload: ReorderItemsCommand['payload'],
  description: string = 'Reorder items'
): ReorderItemsCommand => ({
  type: 'REORDER_ITEMS',
  timestamp: Date.now(),
  description,
  payload,
});

export const createReorderContainersCommand = (
  payload: ReorderContainersCommand['payload'],
  description: string = 'Reorder containers'
): ReorderContainersCommand => ({
  type: 'REORDER_CONTAINERS',
  timestamp: Date.now(),
  description,
  payload,
});

export const createBatchCommand = (
  commands: UndoableCommand[],
  batchDescription: string
): BatchCommand => ({
  type: 'BATCH',
  timestamp: Date.now(),
  description: batchDescription,
  payload: { commands, batchDescription },
});

export const createSelectContainerCommand = (
  payload: SelectContainerCommand['payload']
): SelectContainerCommand => ({
  type: 'SELECT_CONTAINER',
  timestamp: Date.now(),
  description: payload.containerId ? `Select container ${payload.containerId}` : 'Clear container selection',
  payload,
});

export const createSelectItemCommand = (
  payload: SelectItemCommand['payload']
): SelectItemCommand => ({
  type: 'SELECT_ITEM',
  timestamp: Date.now(),
  description: payload.itemId ? `Select item ${payload.itemId}` : 'Clear item selection',
  payload,
});

export const createSetActivePageCommand = (
  payload: SetActivePageCommand['payload']
): SetActivePageCommand => ({
  type: 'SET_ACTIVE_PAGE',
  timestamp: Date.now(),
  description: `Set active page to ${payload.pageId}`,
  payload,
});

export const createMarkDirtyCommand = (
  section: DirtySection,
  canvasId?: string
): MarkDirtyCommand => ({
  type: 'MARK_DIRTY',
  timestamp: Date.now(),
  description: `Mark ${section} as dirty`,
  payload: { section, canvasId },
});

export const createMarkCleanCommand = (
  section: DirtySection,
  canvasId?: string
): MarkCleanCommand => ({
  type: 'MARK_CLEAN',
  timestamp: Date.now(),
  description: `Mark ${section} as clean`,
  payload: { section, canvasId },
});

export const createUpdateHeaderCommand = (
  updates: Partial<HeaderConfigurationAPI>,
  description: string = 'Update header'
): UpdateHeaderCommand => ({
  type: 'UPDATE_HEADER',
  timestamp: Date.now(),
  description,
  payload: { updates },
});

export const createUpdateFooterCommand = (
  updates: Partial<FooterConfigurationAPI>,
  description: string = 'Update footer'
): UpdateFooterCommand => ({
  type: 'UPDATE_FOOTER',
  timestamp: Date.now(),
  description,
  payload: { updates },
});

export const createAddGroupCommand = (
  group: GroupAPI,
  insertIndex?: number
): AddGroupCommand => ({
  type: 'ADD_GROUP',
  timestamp: Date.now(),
  description: `Add group: ${group.name}`,
  payload: { group, insertIndex },
});

export const createAddPageCommand = (
  page: PageAPI,
  groupId: string,
  insertIndex?: number
): AddPageCommand => ({
  type: 'ADD_PAGE',
  timestamp: Date.now(),
  description: `Add page: ${page.name}`,
  payload: { page, groupId, insertIndex },
});

export const createUpdateGroupCommand = (
  groupId: string,
  updates: Partial<Pick<GroupAPI, 'name' | 'orientation' | 'configuration'>>,
  description: string = 'Update group'
): UpdateGroupCommand => ({
  type: 'UPDATE_GROUP',
  timestamp: Date.now(),
  description,
  payload: { groupId, updates },
});

export const createUpdatePageCommand = (
  pageId: string,
  groupId: string,
  updates: Partial<Pick<PageAPI, 'name' | 'configuration'>>,
  description: string = 'Update page'
): UpdatePageCommand => ({
  type: 'UPDATE_PAGE',
  timestamp: Date.now(),
  description,
  payload: { pageId, groupId, updates },
});

export const createUpdateNavigationConfigCommand = (
  updates: Partial<NavigationConfigAPI>,
  description: string = 'Update navigation configuration'
): UpdateNavigationConfigCommand => ({
  type: 'UPDATE_NAVIGATION_CONFIG',
  timestamp: Date.now(),
  description,
  payload: { updates },
});

export const createRemoveGroupCommand = (
  groupId: string,
  description: string = 'Remove group'
): RemoveGroupCommand => ({
  type: 'REMOVE_GROUP',
  timestamp: Date.now(),
  description,
  payload: { groupId },
});

export const createRemovePageCommand = (
  pageId: string,
  groupId: string,
  description: string = 'Remove page'
): RemovePageCommand => ({
  type: 'REMOVE_PAGE',
  timestamp: Date.now(),
  description,
  payload: { pageId, groupId },
});

export const createReorderGroupsCommand = (
  groupIds: string[],
  description: string = 'Reorder groups'
): ReorderGroupsCommand => ({
  type: 'REORDER_GROUPS',
  timestamp: Date.now(),
  description,
  payload: { groupIds },
});

export const createReorderPagesCommand = (
  groupId: string,
  pageIds: string[],
  description: string = 'Reorder pages'
): ReorderPagesCommand => ({
  type: 'REORDER_PAGES',
  timestamp: Date.now(),
  description,
  payload: { groupId, pageIds },
});

export const createMovePageBetweenGroupsCommand = (
  pageId: string,
  fromGroupId: string,
  toGroupId: string,
  toIndex: number,
  description: string = 'Move page to different group'
): MovePageBetweenGroupsCommand => ({
  type: 'MOVE_PAGE_BETWEEN_GROUPS',
  timestamp: Date.now(),
  description,
  payload: { pageId, fromGroupId, toGroupId, toIndex },
});

export const createDuplicatePageCommand = (
  pageId: string,
  groupId: string,
  newPageId: string,
  newCanvasId: string,
  description: string = 'Duplicate page'
): DuplicatePageCommand => ({
  type: 'DUPLICATE_PAGE',
  timestamp: Date.now(),
  description,
  payload: { pageId, groupId, newPageId, newCanvasId },
});

// ============================================================================
// Command Type Guards
// ============================================================================

export const isContainerCommand = (cmd: UIModelCommand): cmd is ContainerCommand =>
  ['ADD_CONTAINER', 'REMOVE_CONTAINER', 'UPDATE_CONTAINER', 'MOVE_CONTAINER', 'REORDER_CONTAINERS', 'DUPLICATE_CONTAINER'].includes(cmd.type);

export const isItemCommand = (cmd: UIModelCommand): cmd is ItemCommand =>
  ['ADD_ITEM', 'REMOVE_ITEM', 'UPDATE_ITEM', 'MOVE_ITEM', 'REORDER_ITEMS'].includes(cmd.type);

export const isNavigationCommand = (cmd: UIModelCommand): cmd is NavigationCommand =>
  ['ADD_GROUP', 'REMOVE_GROUP', 'UPDATE_GROUP', 'REORDER_GROUPS', 'ADD_PAGE', 'REMOVE_PAGE', 'UPDATE_PAGE', 'REORDER_PAGES', 'MOVE_PAGE_BETWEEN_GROUPS', 'DUPLICATE_PAGE', 'UPDATE_NAVIGATION_CONFIG'].includes(cmd.type);

export const isSelectionCommand = (cmd: UIModelCommand): cmd is SelectionCommand =>
  ['SELECT_CONTAINER', 'SELECT_ITEM', 'SELECT_SUBMODEL', 'CLEAR_SELECTION', 'SET_ACTIVE_PAGE'].includes(cmd.type);

export const isStateCommand = (cmd: UIModelCommand): cmd is StateCommand =>
  ['SET_MODEL', 'MARK_DIRTY', 'MARK_CLEAN'].includes(cmd.type);

export const isBatchCommand = (cmd: UIModelCommand): cmd is BatchCommand =>
  cmd.type === 'BATCH';

export const isUndoableCommand = (cmd: UIModelCommand): cmd is UndoableCommand =>
  !isSelectionCommand(cmd) && !isStateCommand(cmd);

export const isHeaderCommand = (cmd: UIModelCommand): cmd is HeaderCommand =>
  cmd.type === 'UPDATE_HEADER';

export const isFooterCommand = (cmd: UIModelCommand): cmd is FooterCommand =>
  cmd.type === 'UPDATE_FOOTER';

// ============================================================================
// Command Utilities
// ============================================================================

/**
 * Get the dirty section affected by a command
 */
export const getDirtySectionForCommand = (cmd: UIModelCommand): DirtySection | null => {
  if (isContainerCommand(cmd) || isItemCommand(cmd)) {
    return 'canvas';
  }
  if (isNavigationCommand(cmd)) {
    return 'navigation';
  }
  if (isHeaderCommand(cmd)) {
    return 'header';
  }
  if (isFooterCommand(cmd)) {
    return 'footer';
  }
  return null;
};

/**
 * Get the canvasId affected by a command (if applicable)
 */
export const getCanvasIdForCommand = (cmd: UIModelCommand): string | null => {
  if ('payload' in cmd) {
    const payload = cmd.payload as Record<string, unknown>;
    if ('canvasId' in payload && typeof payload.canvasId === 'string') {
      return payload.canvasId;
    }
    if ('fromCanvasId' in payload && typeof payload.fromCanvasId === 'string') {
      return payload.fromCanvasId;
    }
  }
  return null;
};
